'use client';
import { ethers, utils } from 'ethers';
import * as React from 'react';
import { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { RuntimeConfiguration } from '@/configuration';
import { encryptWallet } from '@/utils/encrypt.utils';
import { trackGAEvent } from '@/utils/googleAnalytics';
import { WalletProviderType } from '../constants';
import { buyNowABI } from '../constants/contractABI/BuyNowABI';
import { PaymentMethod } from '../constants/paymentMethod';
import { ClaimContainerType } from '../interface';
import { StorageService } from '../services';
import { ethPriceConversion } from '../utils/ethPriceConversion';
import { getBalance } from '../utils/getBalance.utils';
import { CollectionListProps, useCollection } from './CollectionListProvider';
import { useModalState } from './ModalStateProvider';
import { useNftContent } from './NftContextProvider';
import { usePaymentDetails } from './PaymentDetailsProvider';
import { WalletDetailsProps, useWalletDetails } from './WalletDetailsProvider';

export interface SelectedMetaData {
  address: string;
  tokenId: number;
  name: string;
  image: string;
  isFree?: boolean;
  isDiscount?: boolean;
  discountPercentage?: number;
  tokensHold?: number;
  count?: number;
}

export interface Payment {
  tokenGatingError: string;
  isLoading: boolean;
  validation: 'clear' | 'no_balance' | 'wrong_network';
  error: string;
  priceInEth: string;
  loaderTitle: string;
  collectionList: CollectionListProps[];
  listingPrice: string;
  metaDataList: SelectedMetaData[];
  isApproveLoading: boolean;
  isSkipLoading: boolean;
  showInfo: boolean;
  isSkip: boolean;
  isApproved: boolean;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setValidation: React.Dispatch<React.SetStateAction<'clear' | 'no_balance' | 'wrong_network'>>;
  onInitiateBuyNow: (onSuccess?: (hash?: string) => void) => Promise<void>;
  onCheckTokenGatingUsingContract: (data: WalletDetailsProps) => void;
  onClickSkipPaymentMethod: (chainId: number) => Promise<void>;
  onContinuePayment: (id: number) => Promise<void>;
  resetState: () => void;
  onInitiateCoinFlow: () => any;
  onRetry: () => void;
  setShowInfo: React.Dispatch<React.SetStateAction<boolean>>;
  setLoaderTitle: React.Dispatch<React.SetStateAction<string>>;
  setModalState: React.Dispatch<React.SetStateAction<ClaimContainerType>>;
  setPriceInEth: React.Dispatch<React.SetStateAction<string>>;
}

const PaymentContext = createContext<Payment>({} as Payment);

export const PaymentProvider = ({ children }: { children?: React.ReactNode }) => {
  const { walletDetails } = useWalletDetails();
  const { setModalState } = useModalState();
  const {
    selectedNft,
    nftFilter: { isSaleEnded, isSaleStarted }
  } = useNftContent();
  const { paymentDetails, setPaymentDetails } = usePaymentDetails();
  const [tokenGatingError, setTokenGatingError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<'clear' | 'no_balance' | 'wrong_network'>('clear');
  const [error, setError] = useState('');
  const [priceInEth, setPriceInEth] = useState<string>('');
  const [listingPrice] = useState<string>('');
  const [loaderTitle, setLoaderTitle] = useState<string>('');
  const { collectionList } = useCollection();
  const [metaDataList] = useState<SelectedMetaData[]>([]);
  const [isApproveLoading] = useState<boolean>(false);
  const [isSkipLoading, setIsSkipLoading] = useState<boolean>(false);
  const [isSkip, setIsSkip] = useState<boolean>(false);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [isApproved, setIsApproved] = useState<boolean>(false);

  const resetState = useCallback(() => {
    setValidation('clear');
    setError('');
  }, []);

  const getContractDetails = useCallback((signer: any): ethers.Contract => {
    const contract = new ethers.Contract(RuntimeConfiguration.CONTRACT_ADDRESS, buyNowABI, signer);

    return contract;
  }, []);

  const getEthPriceWithBuffer = useCallback(async () => {
    try {
      setIsLoading(true);
      const signer = walletDetails?.provider?.getSigner();
      const contract = getContractDetails(signer);
      const priceDetails = await contract?.getcurrentPrice?.();
      const ethValue = ethers.utils.formatEther(priceDetails[1].toString());

      setPriceInEth(await ethPriceConversion(ethValue));
    } catch (e: any) {
      setIsLoading(false);
      setModalState(ClaimContainerType.ERROR);
      setError('Something went wrong');
    }
  }, [walletDetails, getContractDetails, setModalState]);

  const onHandleError = useCallback(
    (errorMessage: string) => {
      setIsLoading(false);
      const errorMsg = errorMessage?.toLowerCase();

      if (errorMsg?.includes('user rejected') || errorMsg?.includes('user denied')) {
        setModalState(ClaimContainerType.ERROR);
        setError('Transaction request rejected. Please click "Retry" and confirm to proceed.');

        return null;
      }
      if (errorMsg?.includes('underlying network changed')) {
        setModalState(ClaimContainerType.ERROR);
        setError(`Network change detected. Please switch to the correct network and try again.`);
        return null;
      }
      if (errorMessage?.includes('limit reached') || errorMessage?.toLocaleLowerCase()?.includes('limit exceeded')) {
        setModalState(ClaimContainerType.NOT_ELIGIBLE_TOKEN_GATING);
        setTokenGatingError('Your limit of possible mints for this wallet has been reached. Thank you for minting!');
        return null;
      }
      if (
        errorMessage?.includes('claim not started') ||
        errorMessage?.toLocaleLowerCase()?.includes('sale has not yet started')
      ) {
        setModalState(ClaimContainerType.NOT_ELIGIBLE_TOKEN_GATING);
        setTokenGatingError(
          'It seems you are trying to buy before the sale is open. Please try again during the designated sale window.'
        );
        return null;
      }
      if (errorMessage?.toLocaleLowerCase()?.includes('sale has been closed')) {
        setModalState(ClaimContainerType.NOT_ELIGIBLE_TOKEN_GATING);
        setTokenGatingError('The sale is closed. Thank you for your interest.');
        return null;
      }
      if (errorMsg?.includes('invalid nft owner address')) {
        setModalState(ClaimContainerType.ERROR);
        setError('This item has already been sold. Please choose another item and try again.');
        return null;
      }
      if (errorMsg?.includes('called by claimer') || errorMsg?.includes('buyer or admin or owner of this contract')) {
        setModalState(ClaimContainerType.ERROR);
        setError(
          'Your wallet seems to have changed from the one initially connected. Please disconnect and reconnect using the original wallet.'
        );
        return null;
      }
      if (errorMsg?.includes('execution reverted') || errorMsg?.includes('gas required exceeds')) {
        setModalState(ClaimContainerType.ERROR);
        setError(`Insufficient funds to cover gas fees. Please ensure your balance is adequate and try again.`);
        return null;
      }
      if (errorMsg?.includes('signature expired')) {
        setModalState(ClaimContainerType.ERROR);
        setError(`The transaction has timed out. Please try again.`);
        return null;
      }

      if (errorMsg?.includes('unknown account #0')) {
        setModalState(ClaimContainerType.ERROR);
        setError(`Wallet disconnected. Please reconnect your MetaMask wallet to continue.`);
        return null;
      }

      setModalState(ClaimContainerType.ERROR);
      setError(`Something went wrong. Please try again or choose a different item to continue.`);

      return null;
    },
    [setModalState]
  );

  const getAdminSignature = useCallback(
    async (expiryInSeconds: number) => {
      try {
        if (!selectedNft?.token_id) return;
        const rpcProvider = new ethers.providers.JsonRpcProvider(RuntimeConfiguration.RPC_URL);

        const blockNumber = await rpcProvider.getBlockNumber();
        const block = await rpcProvider.getBlock(blockNumber);
        const timestamp = block.timestamp;
        const expiryTime = timestamp + expiryInSeconds;

        const signingDetails = [
          walletDetails.address,
          0,
          expiryTime,
          '12345',
          'ONCHAINBUY',
          walletDetails.networkDetails?.chainID
        ];

        const hashedMessage = ethers.utils.solidityKeccak256(
          ['bytes'],
          [ethers.utils.solidityPack(['address', 'uint16', 'uint32', 'string', 'string', 'uint256'], signingDetails)]
        );
        const signerWallet = new ethers.Wallet(RuntimeConfiguration.PRIVATE_KEY, rpcProvider);
        const sellerSignature = await signerWallet.signMessage(ethers.utils.arrayify(hashedMessage));

        const tokenDetails = [
          selectedNft?.saleId,
          RuntimeConfiguration.OWNER_ADDRESS,
          selectedNft?.token_id,
          1,
          selectedNft?.token_type === 'ERC721' ? 0 : 1,
          walletDetails.address,
          [],
          ethers.constants.AddressZero,
          ethers.utils.parseEther(selectedNft?.price.toString())
        ];

        const sellerSignDetails = [0, expiryTime, '12345', sellerSignature, signerWallet.address];

        return { tokenDetails, sellerSignDetails };
      } catch (e: any) {
        setModalState(ClaimContainerType.ERROR);
        onHandleError(e?.message);

        return null; // Return null or a specific error value
      }
    },
    [
      selectedNft?.token_id,
      walletDetails.address,
      setModalState,
      onHandleError,
      walletDetails.networkDetails?.chainID,
      selectedNft?.saleId,
      selectedNft?.price,
      selectedNft?.token_type
    ]
  );

  const onContinuePayment = useCallback(
    async (chainId: number) => {
      try {
        setIsSkip(false);
        setIsLoading(true);
        await getEthPriceWithBuffer();
        setModalState(ClaimContainerType.PAY_WITH_GAS);
        setIsLoading(false);
        if (walletDetails?.networkDetails?.chainID !== chainId) {
          setValidation('wrong_network');

          return;
        }
        setValidation('clear');
      } catch (e) {
        setIsLoading(false);
        setModalState(ClaimContainerType.ERROR);
        setError('Something went wrong. Please try again.');
      }
    },
    [setModalState, getEthPriceWithBuffer, walletDetails]
  );

  const getSelectedTokenDetails = useCallback(() => {
    let tokenId;
    const signer = walletDetails?.provider?.getSigner();
    const contract = getContractDetails(signer);

    return {
      signer,
      tokenId: Number(selectedNft?.token_id) || tokenId,
      contract
    };
  }, [walletDetails?.provider, getContractDetails, selectedNft?.token_id]);

  const refetchBalance = useCallback(async () => {
    const balance = await getBalance(
      walletDetails.provider,
      walletDetails.address,
      walletDetails.networkDetails?.chainID,
      walletDetails.providerType
    );

    const walletData: WalletDetailsProps = {
      address: walletDetails.address,
      networkDetails: walletDetails.networkDetails,
      provider: null,
      providerType: walletDetails.providerType,
      refetchBalance: false,
      balance: balance ?? {
        native: 0,
        nonNative: 0
      }
    };

    StorageService.walletDetails.setValue(walletData);
    if (balance?.native <= Number(priceInEth)) {
      setValidation('no_balance');

      return false;
    }
    setValidation('clear');

    return true;
  }, [walletDetails, priceInEth]);

  const onInitiateCoinFlow = useCallback(async () => {
    try {
      if (validation === 'no_balance') {
        await refetchBalance();

        return;
      }
      trackGAEvent('Transaction_Initiated', {
        walletAddress: encryptWallet(walletDetails?.address),
        type: paymentDetails?.paymentType,
        price: priceInEth
      });
      setIsLoading(true);
      const { contract } = getSelectedTokenDetails();
      const tokenDetails = await getAdminSignature(900);
      const data = await contract.interface.encodeFunctionData('buy', [tokenDetails]);

      setIsLoading(false);

      return {
        encodeData: data,
        price: priceInEth
      };
    } catch (e: any) {
      trackGAEvent('Transaction_Failed', {
        walletAddress: encryptWallet(walletDetails?.address),
        type: paymentDetails?.paymentType,
        price: priceInEth
      });

      onHandleError(e?.message);
    }
  }, [
    priceInEth,
    validation,
    walletDetails,
    paymentDetails,
    refetchBalance,
    getSelectedTokenDetails,
    getAdminSignature,
    onHandleError
  ]);

  const onInitiateBuyNow = useCallback(
    async (onSuccess?: (hash?: string) => void) => {
      try {
        if (validation === 'no_balance') {
          await refetchBalance();

          return;
        }

        if (walletDetails?.balance?.native <= Number(priceInEth)) {
          setValidation('no_balance');
          StorageService.requiredGas.clear();

          return;
        }

        trackGAEvent('Transaction_Initiated', {
          walletAddress: encryptWallet(walletDetails?.address),
          type: paymentDetails?.paymentType,
          price: priceInEth
        });
        setIsLoading(true);
        const { contract, signer } = getSelectedTokenDetails();
        const { maxFeePerGas, maxPriorityFeePerGas } = await signer.getFeeData();
        const details = await getAdminSignature(600);

        const gasFee = await contract.estimateGas.buy(details?.tokenDetails, 0, details?.sellerSignDetails, {
          value: utils.parseEther(priceInEth)
        });

        const requiredGas = (+gasFee / Math.pow(10, 9)) * 1.05 + +priceInEth;

        //To display currency in error message, 1.05 to avoid gas issue.
        StorageService.requiredGas.setValue(requiredGas * 1.05);

        const walletStorage = StorageService.walletDetails.getValue() as WalletDetailsProps;

        const walletBalance = walletStorage?.balance?.native;

        if (walletBalance <= +requiredGas) {
          setValidation('no_balance');
          setIsLoading(false);

          return;
        }

        const tx = await contract.buy(details?.tokenDetails, 0, details?.sellerSignDetails, {
          value: utils.parseEther(priceInEth),
          maxPriorityFeePerGas,
          maxFeePerGas
        });

        const hash = tx.hash;
        const transactionStatus = await tx.wait();

        if (transactionStatus?.status === 0) {
          setIsLoading(false);
          setModalState(ClaimContainerType.ERROR);
          setError('This NFT has already been purchased. Please select another available NFT.');
        } else {
          // Success
          setIsLoading(false);
          onSuccess?.(hash);
        }
      } catch (e: any) {
        trackGAEvent('Transaction_Failed', {
          walletAddress: encryptWallet(walletDetails?.address),
          type: paymentDetails?.paymentType,
          price: priceInEth
        });
        onHandleError(e?.message);
      }
    },
    [
      validation,
      walletDetails?.balance?.native,
      walletDetails?.address,
      priceInEth,
      paymentDetails?.paymentType,
      getSelectedTokenDetails,
      getAdminSignature,
      refetchBalance,
      setModalState,
      onHandleError
    ]
  );

  const onClickSkipPaymentMethod = useCallback(
    async (chainId: number) => {
      try {
        setIsApproved(true);
        setIsSkipLoading(true);
        setIsLoading(true);
        setIsSkip(true);
        const signer = walletDetails?.provider?.getSigner();
        const contract = getContractDetails(signer);
        const priceDetails = await contract.getListingPrice(ethers.constants.AddressZero);
        const ethValue = ethers.utils.formatEther(Number(priceDetails).toString());

        setPriceInEth(ethValue);
        if (walletDetails?.providerType === WalletProviderType.EMAIL) {
          setPaymentDetails((prev) => ({
            ...prev,
            paymentType: PaymentMethod.creditCard
          }));
        }
        if (walletDetails?.networkDetails?.chainID !== chainId) {
          setValidation('wrong_network');
          setModalState(ClaimContainerType.PAY_WITH_GAS);
          setIsLoading(false);
          setIsSkipLoading(false);

          return;
        }
        setIsLoading(false);
        setIsSkipLoading(false);
        setValidation('clear');
        setModalState(ClaimContainerType.PAY_WITH_GAS);
      } catch (e) {
        setIsLoading(false);
        setIsSkipLoading(false);
        setModalState(ClaimContainerType.ERROR);
        setError('Something went wrong. Please try again.');
      }
    },
    [setModalState, getContractDetails, setPaymentDetails, walletDetails]
  );

  const onCheckTokenGatingUsingContract = useCallback(
    async (data: WalletDetailsProps) => {
      setTokenGatingError('');
      setError('');
      setLoaderTitle('Processing...');
      setModalState(ClaimContainerType.LOADING);

      try {
        if (isSaleStarted && !isSaleEnded) {
          setIsApproved(true);
          if (data?.providerType === WalletProviderType.EMAIL) {
            setPaymentDetails((prev) => ({
              ...prev,
              paymentType: PaymentMethod.creditCard
            }));
          }
          setPriceInEth(selectedNft?.price.toString() ?? '0');
          setModalState(ClaimContainerType.PAY_WITH_GAS);
        } else {
          throw new Error('The sale has not started or has already ended. Please check the sale status and try again.');
        }
      } catch (e: any) {
        if (e?.message?.toLowerCase()?.includes('unknown account')) {
          setModalState(ClaimContainerType.ERROR);
          setError('Your wallet got disconnected. Please "Log Out" and try again');
        } else {
          setModalState(ClaimContainerType.ERROR);
          onHandleError(e?.message);
        }
      }
    },
    [setModalState, setPaymentDetails, isSaleEnded, isSaleStarted, selectedNft?.price, onHandleError]
  );

  const onRetry = useCallback(async () => {
    resetState();
    if (walletDetails?.address) {
      onCheckTokenGatingUsingContract(walletDetails);
    } else {
      setModalState(ClaimContainerType.EXISTING_WALLET);
    }
  }, [walletDetails, setModalState, resetState, onCheckTokenGatingUsingContract]);

  const values = useMemo<Payment>(() => {
    return {
      tokenGatingError,
      isLoading,
      validation,
      error,
      priceInEth,
      loaderTitle,
      collectionList,
      listingPrice,
      metaDataList,
      isApproveLoading,
      isSkipLoading,
      showInfo,
      isSkip,
      isApproved,
      setError,
      setValidation,
      setModalState,
      onInitiateBuyNow,
      onCheckTokenGatingUsingContract,
      onClickSkipPaymentMethod,
      onContinuePayment,
      resetState,
      onInitiateCoinFlow,
      onRetry,
      setShowInfo,
      setLoaderTitle,
      setPriceInEth
    };
  }, [
    tokenGatingError,
    isLoading,
    validation,
    error,
    priceInEth,
    loaderTitle,
    collectionList,
    listingPrice,
    metaDataList,
    isApproveLoading,
    isSkipLoading,
    showInfo,
    isSkip,
    isApproved,
    onInitiateBuyNow,
    onCheckTokenGatingUsingContract,
    onClickSkipPaymentMethod,
    onContinuePayment,
    resetState,
    onInitiateCoinFlow,
    onRetry,
    setShowInfo,
    setLoaderTitle,
    setModalState,
    setPriceInEth
  ]);

  return <PaymentContext.Provider value={values}>{children}</PaymentContext.Provider>;
};

export const usePayment = () => {
  return useContext(PaymentContext);
};
